import React from 'react';

import moment from 'moment';

import { GetFluxNextExecutionTimeResponseDtoFluxTypeEnum } from 'lib_api/lib/api/gen';

import {
  ColumnProps,
  TablePagination,
  TableSort,
} from 'components/WrappedComponents/Table/types';
import useNextExecutionTime from 'hooks/flux/useNextExecutionTime';
import { PaginationKey } from 'hooks/PaginationStoreContext';
import { useDashboardSearchWithSort } from 'hooks/search/useDashboardSearchWithSort';
import { SearchTypeWithSort } from 'types/searchType';
import { DATE_FORMAT } from 'utils/formats';

import ErrorCell from '../../../components/ErrorCell/ErrorCell';
import DashboardFlux from '../../../DashboardFlux';
import { FilterDashboardFlux } from '../../../types';
import { statusIconMapping } from '../../../utils';
import {
  FetchFluxResponse,
  FluxAlienationDto,
  SearchAlienationResponse,
} from '../types';

interface DashboardFluxAlienationProps<
  FluxDto extends FluxAlienationDto,
  SortOptions extends string,
> {
  useSearchFluxs: (
    _: FilterDashboardFlux,
    sorts: TableSort<SortOptions>,
    pagination: TablePagination,
  ) => SearchAlienationResponse<FluxDto>;
  buildSpecificTableColumns: () => ColumnProps<FluxDto, SortOptions>[];
  tableTitle: string;
  fluxType: GetFluxNextExecutionTimeResponseDtoFluxTypeEnum;
  keyPagination: PaginationKey;
}

// as in this file are here because we didn't manage to make TS understand we want SortOptions that are of the form 'DATE_APPEL' | XXX | YYY
function DashboardFluxAlienation<
  FluxDto extends FluxAlienationDto,
  SortOptions extends string,
>({
  useSearchFluxs,
  buildSpecificTableColumns,
  tableTitle,
  fluxType,
  keyPagination,
}: DashboardFluxAlienationProps<FluxDto, SortOptions>): React.ReactElement {
  const searchFluxDomaine: SearchTypeWithSort<
    FluxDto,
    Record<string, unknown>,
    SortOptions
  > = useDashboardSearchWithSort<
    FluxDto,
    Record<string, unknown>,
    SortOptions,
    FetchFluxResponse<FluxDto>
  >(
    useSearchFluxs,
    {},
    {
      DATE_APPEL: false,
    } as Partial<TableSort<SortOptions>>,
    keyPagination,
  );

  function buildDetailsContent(data: FluxDto): React.ReactElement {
    return <ErrorCell flux={data} />;
  }

  function computeBadge(flux: FluxDto): React.ReactNode {
    if (flux.error) {
      return statusIconMapping.ERROR;
    } else if (flux.nbWarnings && flux.nbWarnings > 0) {
      return statusIconMapping.WARNING;
    } else {
      return statusIconMapping.SUCCESS;
    }
  }

  const dateProchaineExecution = useNextExecutionTime(fluxType);

  function buildColumns(): ColumnProps<FluxDto, SortOptions>[] {
    return [
      {
        title: '',
        buildCellContent: data => computeBadge(data),
      },
      {
        title: 'Appel',
        buildCellContent: data => moment(data.dateAppel).format(DATE_FORMAT),
        sortedField: 'DATE_APPEL' as SortOptions,
      },
      ...buildSpecificTableColumns(),
      {
        title: "Type d'erreur",
        buildCellContent: data => buildDetailsContent(data),
      },
    ];
  }

  return (
    <DashboardFlux
      useSearchFlux={searchFluxDomaine}
      buildColumns={buildColumns}
      title={tableTitle}
      buildItemKey={(item: FluxDto) => item.id}
      dateProchaineExecution={dateProchaineExecution}
    />
  );
}

export default DashboardFluxAlienation;
